import type { SupabaseClient } from "@supabase/supabase-js"

import type { OnboardingGoal } from "@/types/profile"

/** Customer-facing `visa_metadata` rows only (task seeding, onboarding route picker, summaries). */
export const VISA_METADATA_ENRICHMENT_LIVE = "live" as const
export const VISA_METADATA_VERIFICATION_VERIFIED = "verified" as const

/** UI / legacy slugs → `visa_metadata.visa_type` values used in the database. */
export const UI_TO_DB_VISA_TYPE_MAP: Record<string, string> = {
  tourist: "visitor",
  visit: "visitor",
  leisure: "visitor",
  holiday: "visitor",
  study: "student",
  student: "student",
  education: "student",
  work: "work",
  employment: "work",
  business: "work",
}

/**
 * Maps friendly or legacy visa type strings to DB `visa_type` before `visa_metadata` lookups
 * (e.g. `tourist` → `visitor`). Unknown values are returned trimmed unchanged.
 */
export function normalizeVisaTypeForDB(uiType: string): string {
  const trimmed = String(uiType ?? "").trim()
  if (!trimmed) return ""
  const key = trimmed.toLowerCase()
  return UI_TO_DB_VISA_TYPE_MAP[key] ?? trimmed
}

const TOURISM_VISA_TYPE_SYNONYMS = new Set(["visitor", "tourist"])

function visaTypeSlugMatchesRouteRow(dbSlug: string, requested: string): boolean {
  const d = dbSlug.trim().toLowerCase()
  const r = requested.trim().toLowerCase()
  if (!d || !r) return false
  if (d === r) return true
  if (TOURISM_VISA_TYPE_SYNONYMS.has(d) && TOURISM_VISA_TYPE_SYNONYMS.has(r)) return true
  return false
}

export type VisaMetadataRouteRow = {
  id?: unknown
  visa_type?: unknown
  requirements?: unknown
  required_documents?: unknown
  process_steps?: unknown
  /** ISO 4217 from `visa_metadata.destination_currency` when present. */
  destination_currency?: unknown
}

/** Result of {@link findVisaMetadataRowForRoute}: `queryError` is set only when the Supabase request fails. */
export type VisaMetadataRouteLookup = {
  row: VisaMetadataRouteRow | null
  queryError: string | null
}

/**
 * Loads the `visa_metadata` row for a route, matching `visa_type` with case-insensitive equality
 * and treating `visitor` / `tourist` as interchangeable tourism slugs (datasets vary).
 */
export async function findVisaMetadataRowForRoute(
  client: SupabaseClient,
  nationalityCode: string,
  destinationCode: string,
  visaType: string
): Promise<VisaMetadataRouteLookup> {
  const nat = nationalityCode.trim().toUpperCase()
  const dest = destinationCode.trim().toUpperCase()
  const vt = visaType.trim()
  if (!nat || !dest || !vt) {
    return { row: null, queryError: null }
  }

  // Use `*` so older DBs without optional columns (e.g. destination_currency) do not fail the whole route lookup.
  const { data, error } = await client
    .from("visa_metadata")
    .select("*")
    .eq("nationality_code", nat)
    .eq("destination_code", dest)
    .in("goal_type", ["tourism", "digital_nomad", "study", "relocation"])
    .eq("enrichment_status", VISA_METADATA_ENRICHMENT_LIVE)
    .eq("verification_status", VISA_METADATA_VERIFICATION_VERIFIED)

  if (error) {
    const parts = [error.message, error.details].filter((p): p is string => typeof p === "string" && p.trim() !== "")
    return { row: null, queryError: parts.join(" — ") || "visa_metadata query failed" }
  }

  if (!data?.length) {
    return { row: null, queryError: null }
  }

  for (const row of data as VisaMetadataRouteRow[]) {
    const slug = typeof row.visa_type === "string" ? row.visa_type : ""
    if (visaTypeSlugMatchesRouteRow(slug, vt)) {
      return { row, queryError: null }
    }
  }
  return { row: null, queryError: null }
}

export type VisaTypeOption = {
  visa_type: string
  visa_status: string
  label: string
}

function capitalize(raw: string): string {
  const s = String(raw ?? "").trim()
  if (!s) return ""
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export function visaTypeToLabel(visaType: string): string {
  const v = String(visaType ?? "").trim().toLowerCase()
  if (!v) return ""
  if (v === "visitor") return "Turismo / Visita"
  if (v === "work") return "Trabalho"
  if (v === "student") return "Estudos"
  if (v === "tourist") return "Turismo"
  return capitalize(v)
}

/** Onboarding goal slug → `visa_metadata.visa_type` values for route picker filtering. */
const GOAL_VISA_TYPE_DB_FILTER: Record<string, string[]> = {
  digital_nomad: ["digital_nomad", "work"],
  study: ["student"],
  relocation: ["relocation", "work"],
  trip: ["visitor", "tourist"],
}

function dedupeAndSortVisaTypeOptions(out: VisaTypeOption[]): VisaTypeOption[] {
  const seen = new Set<string>()
  const unique = out.filter((o) => {
    const k = o.visa_type.toLowerCase()
    if (seen.has(k)) return false
    seen.add(k)
    return true
  })
  unique.sort((a, b) => a.label.localeCompare(b.label, "pt-BR", { sensitivity: "base" }))
  return unique
}

async function queryVisaTypesForRoute(
  nat: string,
  dest: string,
  client: SupabaseClient,
  visaTypeFilter?: string[]
): Promise<VisaTypeOption[]> {
  let query = client
    .from("visa_metadata")
    .select("visa_type, requirements")
    .eq("nationality_code", nat)
    .eq("destination_code", dest)
    .in("goal_type", ["tourism", "digital_nomad", "study", "relocation"])
    .eq("enrichment_status", VISA_METADATA_ENRICHMENT_LIVE)
    .eq("verification_status", VISA_METADATA_VERIFICATION_VERIFIED)

  if (visaTypeFilter?.length) {
    query = query.in("visa_type", visaTypeFilter)
  }

  const { data, error } = await query
  if (error) return []

  const rows = (data ?? []) as Array<{ visa_type?: unknown; requirements?: unknown }>
  const out: VisaTypeOption[] = []
  for (const r of rows) {
    const visa_type = typeof r.visa_type === "string" ? r.visa_type.trim() : ""
    if (!visa_type) continue
    const req = r.requirements as { visa_status?: unknown } | null
    const visa_status = typeof req?.visa_status === "string" ? req.visa_status.trim() : ""
    out.push({
      visa_type,
      visa_status,
      label: visaTypeToLabel(visa_type),
    })
  }

  return dedupeAndSortVisaTypeOptions(out)
}

/**
 * Fetch available `visa_type` values for nationality × destination route
 * from `visa_metadata` (ISO codes). When `goalType` is set, filters by goal-aligned
 * visa types and falls back to all route types if none match.
 */
export async function getVisaTypesForRoute(
  nationality: string,
  destination: string,
  client: SupabaseClient,
  goalType?: string
): Promise<VisaTypeOption[]> {
  const nat = nationality.trim().toUpperCase()
  const dest = destination.trim().toUpperCase()
  if (!nat || !dest) return []

  const goalKey = goalType?.trim()
  const visaTypeFilter = goalKey ? GOAL_VISA_TYPE_DB_FILTER[goalKey] : undefined

  if (visaTypeFilter?.length) {
    const filtered = await queryVisaTypesForRoute(nat, dest, client, visaTypeFilter)
    if (filtered.length > 0) return filtered
    return queryVisaTypesForRoute(nat, dest, client)
  }

  return queryVisaTypesForRoute(nat, dest, client)
}

/** Goal-only UI filter on canonical `visa_type` slugs from `visa_metadata` (step 2 + step 5). */
export function filterVisaTypeOptionsByGoal(
  goal: OnboardingGoal | null | undefined,
  options: VisaTypeOption[]
): VisaTypeOption[] {
  if (!options.length) return []

  const hay = (s: string) =>
    s
      .toLowerCase()
      .normalize("NFD")
      .replace(/\p{M}/gu, "")
      .replace(/[_-]+/g, " ")

  const goalPatterns: Record<OnboardingGoal, RegExp[]> = {
    trip: [
      /tourist|tourism|visitor|visit|short stay|short-stay|transit|business|schengen|etias|eta\b/,
    ],
    relocation: [
      /digital nomad|nomad|\bd7\b|work|employment|residen|residence|temporary|long stay|long-stay|national visa|residency/,
    ],
    study: [/student|study|education|university|school|exchange|scholar/],
    family: [/family|spouse|partner|dependent|reunification|reuniao|reunificacao/],
    exploring: [],
    digital_nomad: [
      /digital nomad|nomad|\bd7\b|remote work|freelance/,
    ],
  }

  const patterns = goal ? goalPatterns[goal] : []
  if (!patterns || patterns.length === 0) return options

  const out = options.filter((o) => patterns.some((re) => re.test(hay(o.visa_type))))
  return out.length > 0 ? out : options
}

/** Fields needed to resolve a `visa_metadata.visa_type` for workspace provisioning. */
export type CanonicalVisaPickInput = {
  canonicalVisaTypesForRoute?: string[] | undefined
  visaType?: string | null
  existingVisa?: string | null
}

/**
 * Picks a `visa_type` that exists on the route in `visa_metadata`, using step 5 then step 2 selection.
 * Returns "" when nothing valid is selected (callers must not fall back to an arbitrary type like `visitor`).
 * If exactly one canonical type exists and the user did not pick anything, returns that single type.
 * If the user picked a value that is not in `canonicalVisaTypesForRoute`, returns "" (even when only one canonical type exists).
 */
export function resolveCanonicalVisaTypeForProvision(input: CanonicalVisaPickInput): string {
  const canonical = (input.canonicalVisaTypesForRoute ?? []).map((x) => x.trim()).filter(Boolean)
  const set = new Set(canonical.map((x) => x.toLowerCase()))
  for (const c of [input.existingVisa, input.visaType]) {
    const t = c?.trim() ?? ""
    if (!t) continue
    const lower = t.toLowerCase()
    if (set.has(lower)) {
      return canonical.find((x) => x.toLowerCase() === lower) ?? t
    }
  }
  const hadAnySelection = Boolean(input.existingVisa?.trim() || input.visaType?.trim())
  if (!hadAnySelection && canonical.length === 1) return canonical[0] ?? ""
  return ""
}

